import React, { useEffect, useState } from 'react';
import { fetchAndConvertHeicToPng } from 'utils/convertData';
import { CircularProgress } from '@mui/material';

type ClientLogoProps = {
  url: string;
  type?: string;
  alt: string;
  height?: string | number;
};

/**
 * Component to display a client logo.
 * @param url
 * @param type
 * @param alt
 * @param height
 * @constructor
 */
export const ClientLogo: React.FC<ClientLogoProps> = ({ url, type, alt, height = 20 }) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    let revokeUrl: string | undefined;
    setLoading(true);

    const detectType = async () => {
      if (type) return type;
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        return blob.type;
      } catch {
        return '';
      }
    };

    const loadImage = async () => {
      const imgType = type || (await detectType());
      try {
        if (imgType === 'image/heic') {
          const pngUrl = await fetchAndConvertHeicToPng(url);
          setImageSrc(pngUrl);
          revokeUrl = pngUrl;
        } else {
          setImageSrc(url);
        }
      } finally {
        setLoading(false);
      }
    };

    loadImage();
    return () => {
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [url, type]);

  if (loading) {
    return <CircularProgress size={20} />;
  }
  if (!imageSrc) {
    return null;
  }
  return <img height={height} src={imageSrc} alt={alt} />;
};
